/**
 * 次の睡眠までを出す Home ウィジェット。
 *
 * 睡眠プラン（予定に合わせた就寝・起床）から、就寝までの残り時間と寝る準備・起床の時刻を出す。
 * 計画はカレンダーを読むので重く、バックエンドで数分使い回したものを取る。
 */

import { format } from 'date-fns'
import { AlertTriangle, BedDouble, Moon, Sunrise } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import type { SleepPlanWindow } from '@/api/types/sleep'
import type { NavigateFn } from '@/components/home/home-widget-data'
import { cardListRows, isRow, isTile } from '@/components/home/home-widget-data'
import { WidgetCard, WidgetEmpty, WidgetRow, WidgetTile } from '@/components/home/widget-card'
import { formatDurationMinutes } from '@/lib/activity-format-utils'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'
import type { WidgetSize } from '@/store/useHomeLayoutStore'
import { useSleepStore } from '@/store/useSleepStore'

const TICK_MS = 30_000
/** 計画を取り直す間隔（バックエンドの使い回しと同じ 10 分） */
const STALE_MS = 10 * 60 * 1000
/** 取得に失敗したときに次に試すまでの間隔 */
const RETRY_MS = 60 * 1000
const LABEL = '次の睡眠'
const PLAN_PATH = `${PATHS.SLEEP.HOME}?tab=today`

type Phase = 'before' | 'windDown' | 'asleep'

type Status = {
  phase: Phase
  /** 次の区切り（準備中・それ以前は就寝、睡眠中は起床）までの分 */
  remainingMin: number
}

const statusOf = (night: SleepPlanWindow, now: number): Status | null => {
  const bed = parseUtcDate(night.bedtime_at).getTime()
  const wake = parseUtcDate(night.wake_at).getTime()
  if (now >= wake) return null
  if (now >= bed) return { phase: 'asleep', remainingMin: (wake - now) / 60_000 }
  const phase = now >= parseUtcDate(night.wind_down_at).getTime() ? 'windDown' : 'before'
  return { phase, remainingMin: (bed - now) / 60_000 }
}

const PHASE_LABEL = new Map<Phase, string>([
  ['before', '就寝まで'],
  ['windDown', '寝る準備の時間'],
  ['asleep', '起床まで'],
])

const PHASE_ACCENT = new Map<Phase, string>([
  ['before', 'text-indigo-400'],
  ['windDown', 'text-amber-400'],
  ['asleep', 'text-sky-400'],
])

const clock = (iso: string): string => format(parseUtcDate(iso), 'H:mm')

/** 1x1 用の短い残り時間（2:05 / 45分） */
const shortCountdown = (minutes: number): string => {
  const total = Math.max(Math.floor(minutes), 0)
  if (total < 60) return `${total}分`
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

const useNow = (): number => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const update = () => setNow(Date.now())
    const id = window.setInterval(update, TICK_MS)
    document.addEventListener('visibilitychange', update)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', update)
    }
  }, [])
  return now
}

/** 計画が無い・古い・起床を過ぎたら取り直す */
const useFreshPlan = (now: number): SleepPlanWindow | null => {
  const plan = useSleepStore((s) => s.plan)
  const fetchedAt = useSleepStore((s) => s.planFetchedAt)
  const fetchPlan = useSleepStore((s) => s.fetchPlan)
  const attemptedAtRef = useRef(0)
  const night = plan?.window ?? null

  useEffect(() => {
    const passed = night !== null && parseUtcDate(night.wake_at).getTime() <= now
    const stale = plan === null || now - fetchedAt > STALE_MS || passed
    if (!stale || now - attemptedAtRef.current < RETRY_MS) return
    attemptedAtRef.current = now
    void fetchPlan(true)
  }, [now, plan, night, fetchedAt, fetchPlan])

  return night
}

export const SleepWidget = ({ size, onNavigate }: { size: WidgetSize; onNavigate: NavigateFn }) => {
  const now = useNow()
  const night = useFreshPlan(now)
  const steps = useSleepStore((s) => s.plan?.prep_steps)
  const status = night ? statusOf(night, now) : null
  const open = () => onNavigate(PLAN_PATH)
  const accent = status ? PHASE_ACCENT.get(status.phase) : 'text-indigo-400'
  const label = status ? (PHASE_LABEL.get(status.phase) ?? LABEL) : LABEL

  if (isTile(size)) {
    return (
      <WidgetTile
        icon={Moon}
        label={label}
        value={status ? shortCountdown(status.remainingMin) : '-'}
        accentClassName={accent}
        onClick={open}
      />
    )
  }

  if (isRow(size)) {
    return (
      <WidgetRow
        icon={Moon}
        label={label}
        value={status ? shortCountdown(status.remainingMin) : '-'}
        sub={night ? `就寝 ${clock(night.bedtime_at)}` : undefined}
        accentClassName={accent}
        onClick={open}
      >
        {night && (
          <div className="space-y-0.5 text-[11px] tabular-nums">
            <div className="truncate text-muted-foreground">
              準備 {clock(night.wind_down_at)}〜 / 起床 {clock(night.wake_at)}
            </div>
            <div className={cn('truncate', night.warning ? 'text-rose-400' : 'text-muted-foreground')}>
              {formatDurationMinutes(night.sleep_min)}
              {night.warning && ' 短め'}
            </div>
          </div>
        )}
      </WidgetRow>
    )
  }

  const upcomingSteps = (steps ?? [])
    .filter((step) => parseUtcDate(step.at).getTime() > now)
    .slice(0, cardListRows(size))

  return (
    <WidgetCard icon={Moon} label={LABEL} onClick={open} accentClassName={accent}>
      {!night || !status ? (
        <WidgetEmpty message="計画なし" />
      ) : (
        <div className="flex h-full flex-col gap-1.5">
          <div>
            <div className={cn('text-[10px]', accent)}>{label}</div>
            <div className="text-xl leading-tight font-semibold tabular-nums">
              {formatDurationMinutes(Math.max(Math.floor(status.remainingMin), 0))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1 text-center tabular-nums">
            {[
              { icon: BedDouble, title: '準備', at: night.wind_down_at },
              { icon: Moon, title: '就寝', at: night.bedtime_at },
              { icon: Sunrise, title: '起床', at: night.wake_at },
            ].map(({ icon: Icon, title, at }) => (
              <div key={title} className="rounded-md bg-muted/60 py-1">
                <div className="flex items-center justify-center gap-0.5 text-[9px] text-muted-foreground">
                  <Icon size={10} />
                  {title}
                </div>
                <div className="text-xs font-semibold">{clock(at)}</div>
              </div>
            ))}
          </div>
          <div
            className={cn(
              'flex items-center gap-1 truncate text-[10px]',
              night.warning ? 'text-rose-400' : 'text-muted-foreground',
            )}
          >
            {night.warning && <AlertTriangle size={10} className="shrink-0" />}
            <span className="truncate">
              睡眠 {formatDurationMinutes(night.sleep_min)}
              {night.shift_min <= -60 && `・${formatDurationMinutes(-night.shift_min)}早め`}
            </span>
          </div>
          {upcomingSteps.length > 0 && (
            <ul className="flex flex-col gap-0.5 border-t pt-1">
              {upcomingSteps.map((step) => (
                <li key={step.title} className="flex items-center gap-1.5 text-xs">
                  <span className="w-9 shrink-0 text-[10px] text-muted-foreground tabular-nums">{clock(step.at)}</span>
                  <span className="truncate">{step.title}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </WidgetCard>
  )
}
